'use client'

import { CollectionRowsSkeleton } from '@/components/collection-loading-skeleton'
import useCollectionUrlState from '@/utils/hooks/useCollectionUrlState'
import InstantSell from '@/components/offers/instant-sell'
import MakeOfferButton from '@/components/offers/make-offer-button'
import tradeStyles from '@/components/offers/trade-panel.module.css'
import MarketTile from '@/components/offers/market-tile'
import React, { FC, ReactElement, useState, useCallback, useMemo, useEffect } from 'react'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Slider } from '@/components/ui/slider'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@/components/ui/select'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow
} from '@/components/ui/table'
import { MARKET_ROYALTY_ADDRESS, TOOL_FEE_ADDRESS } from '@/utils/constants'
import useCollectionInscriptions from '@/utils/hooks/useCollectionInscriptions'
import useSWR from 'swr'
import useEscrows from '@/utils/hooks/useEscrows'
import useRuneStats from '@/utils/hooks/useRuneStats'
import isRune from '@/utils/is-rune'
import isAlkanes from '@/utils/is-alkanes'
import { Collection } from '@/utils/types/collections'
import { Inscription } from '@/utils/types/inscription'
import { Attribute } from '@/utils/types/collections'
import InscriptionContent from '@/components/inscription-content'
import InscriptionActions from '@/components/inscription-actions'
import BitmapArt from '@/components/bitmap-art'
import SecureListingBadge from '@/components/secure-listing-badge'
import BitmapExplorerBar from '@/components/bitmap/bitmap-explorer-bar'
import RemoteCollectionFeed from '../remote-collection-feed'
import TiltCard from '@/components/bitmap-art/tilt-card'
import useSelect from '@/components/search-input/hooks/useSelect'
import Invoice from '@/components/search-input/components/invoice'
import { formatSatoshis } from '@/utils/hooks/useSatoshiPrice'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import {
	NETWORK,
	MARKET_FEE_PERCENTAGE,
	MARKET_FEE_RATE,
	REMOTE_SLUGS,
	API_URL
} from '@/utils/constants'
import numberWithCommas from '@/utils/format-number'
import Wallet from '@/utils/wallet'
import event from '@/utils/event'
import CollectionDetail from '@/components/search-input/pages/collection-detail'
import { formatSatsPer } from '@/utils/format-sats'
import { formatUsdDisplay, formatUsdPer, formatUsdPerBillion } from '@/utils/format-usd'
import BitcoinIcon from '@/icons/bitcoin-filled'
import useInscriptionModal from '@/utils/hooks/useInscriptionModal'
import useCollectionRanks from '@/utils/hooks/useCollectionRanks'
import parseDate from '@/utils/parse-date'

const RARITY_FILTER_TRAIT = 'Rarity'
const RARITY_TIER_OPTIONS = ['Exotic', 'Legendary', 'Epic', 'Rare', 'Uncommon', 'Common']
import SelectButton from '@/components/search-input/components/select-button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import InfiniteScroll from 'react-infinite-scroll-component'
import featureFlags from '@/utils/feature-flags'
import classNames from 'classnames'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import useDebounce from '@/utils/hooks/useDebounce'
import useWindowSize from '@/utils/hooks/useWindowSize'
import Link from 'next/link'
import axios from 'axios'
import { TapSwapWrapper } from '../tap-swap-wrapper'
import { Filter, List as ListIcon, LayoutGrid } from 'lucide-react'
import TraitFilters from '@/components/trait-filters'
import { useMonkeFilters, useMonkeCatalog, useSavedMonkes } from '@/utils/hooks/useMonkeDiscovery'
import { isDiscoveryFilter, matchesDiscoveryFilters } from '@/utils/nodemonkes'
import MonkeDiscoveryControls from '@/components/nodemonkes/discovery-controls'

import { onKeyboardClick } from '@/utils/a11y/keyboard-click'
import {
	buildBitmapFilterAttributes,
	matchesBitmapNumberFilters,
	splitBitmapFilters,
	expandBitmapAttributeFilters,
	matchesBitmapAttributeFilter
} from '@/utils/bitmap-filters'
import { isCollectionObjectSlug } from '@/utils/collection-object'
import {
	isSnipeProtectedListing,
	protocolFromSlug,
	resolveBitcoinOutpoint,
	secureErrorMessage
} from '@/utils/market/secure-purchase'
import {
	buildSecurePurchase,
	secureInvoice,
	type SecurePurchaseQuote,
	signSecurePurchase
} from '@/utils/market/secure-ticket-client'
import { snipeProtectionInvoiceItem } from '@/components/snipe-protection-toggle'
import { isMultiStepCheckout, planCheckout, type CheckoutStep } from '@/utils/market/checkout-plan'
import {
	beginWalletFlow,
	classifyWalletError,
	finishWalletFlow,
	itemCountBucket,
	sanitizeCollectionSlug,
	tapProviderDetails
} from '@/utils/wallet/telemetry'
import type { WalletTelemetryAssetProtocol } from '@/utils/wallet/telemetry-schema'
const EVENT = 'add-to-cart'

interface FeedProps {
	height: string
	title: string
	items: any[]
	slug: string
	supply: number
	listed: number
	meta: Collection
	isMobile: boolean
	attributes?: Attribute[]
	satributes?: any[]
	onSelectBulk: (id: Inscription) => void
	bulkSelected: any
	isFungible: boolean
	isRune: boolean
	onSelectionCountChange?: (count: number) => void
	initialSelectedId?: string
	/** Minimal mode: hides tabs, slider, and shows only cart items */
	minimal?: boolean
	/** Cart items to show and auto-select in minimal mode */
	cartItems?: string[]
}

const getInscriptionSortValue = (inscription: any): number => {
	const rawNum = inscription?.num
	if (typeof rawNum === 'number' && Number.isFinite(rawNum)) return rawNum

	if (typeof rawNum === 'string') {
		const parsed = Number.parseInt(rawNum, 10)
		if (Number.isFinite(parsed)) return parsed
	}

	const name = inscription?.meta?.name || inscription?.escrow?.name || inscription?.name
	const match = name?.match?.(/^(\d+)\.bitmap$/i)
	return match ? parseInt(match[1], 10) : Number.MAX_VALUE
}

export const Feed: FC<FeedProps> = (props): ReactElement => {
	const { isMobile: isMobileViewport } = useWindowSize()
	// Treat as mobile when SSR UA matches OR the client viewport is mobile-sized.
	// SSR-only detection misses desktop browsers narrowed to mobile widths and
	// leaves a nested scroll container fighting page scroll on touch devices.
	const isMobile = props.isMobile || isMobileViewport
	const [selectedTab, setSelectedTab] = useCollectionUrlState('trade', 'buy', ['buy', 'swap'], !props.minimal)
	// Mobile starts in list view; the toggle still offers grid browsing.
	const [viewMode, setViewMode] = useCollectionUrlState('view', 'list', ['list', 'grid'], !props.minimal)
	// When true, the (mobile, NFT) grid shows the full collection rather than
	// only listed items — parity with desktop's "Show All" default.
	const showAllMobileGrid = isMobile && !props.isFungible && viewMode === 'grid'
	const [selected, setSelected] = React.useState('PriceAsc')
	const [_invoiceOpen, _setInvoiceOpen] = useState(false)
	const [invoiceError, setInvoiceError] = useState<string | null>(null)
	const [isRebuilding, setIsRebuilding] = useState(false)
	const [displayCount, setDisplayCount] = useState(32)
	const [recentlySelected, setRecentlySelected] = useState(new Set<string>())
	const [selectionOrder, setSelectionOrder] = useState<string[]>([])
	const [builtPsbt, setBuiltPsbt] = useState<string | null>(null)
	const builtSetupRef = React.useRef<string | null>(null)
	const secureQuoteRef = React.useRef<SecurePurchaseQuote | null>(null)
	// Multi-step checkout for carts that mix legacy and snipe-protected
	// listings (or hold more protected ones than one purchase allows). The
	// plan is fixed when the cart is first built; the selection is narrowed
	// to one step at a time and advanced on `purchase-completed`. A
	// user-driven selection change drops it.
	const [checkoutPlan, setCheckoutPlan] = useState<{ steps: CheckoutStep[]; index: number } | null>(
		null
	)
	const checkoutPlanRef = React.useRef<{ steps: CheckoutStep[]; index: number } | null>(null)
	useEffect(() => {
		checkoutPlanRef.current = checkoutPlan
	}, [checkoutPlan])
	const [builtFee, setBuiltFee] = useState<number>(0)
	const [builtRebarFee, setBuiltRebarFee] = useState<number>(0)
	const [builtRebar, setBuiltRebar] = useState(false)
	const [imageModalOpen, setImageModalOpen] = useState(false)
	const [selectedImage, setSelectedImage] = useState<any>(null)
	const [filters, setFilters] = useMonkeFilters(props.slug)
	const { byId: monkeCatalog } = useMonkeCatalog(props.slug === 'nodemonkes')
	const { saved: savedMonkes } = useSavedMonkes()
	const [_searchInput, _setSearchInput] = useState('')
	const [search, _setSearch] = useState('')
	const debouncedSearch = useDebounce(search, 300)
	const [filterSheetOpen, setFilterSheetOpen] = useState(false)

	const { exchangeRate, isLoading: isExchangeRateLoading } = useExchangeRate()
	const openInscriptionModal = useInscriptionModal()
	const { data: ranksData } = useCollectionRanks(props.slug)

	const isBitmap = props.slug === 'bitmap'
	const isRemoteCollection = REMOTE_SLUGS.includes(props.slug) || isCollectionObjectSlug(props.slug)
	const isVirtualObject = isCollectionObjectSlug(props.slug)
	const isTapDmtNat = props.slug?.toUpperCase() === 'TAP-DMT-NAT'

	// Fetch fresh attributes for bitmap (same as RemoteCollectionFeed)
	const { data: freshAttributes } = useSWR(
		isBitmap ? `${API_URL}/collection/${props.slug}/attributes` : null,
		(url: string) => fetch(url).then((r) => r.json()),
		{ fallbackData: props.attributes, refreshInterval: 600_000 }
	)

	const bitmapAttributes = useMemo(() => {
		if (!isBitmap) return props.attributes || []
		return buildBitmapFilterAttributes(freshAttributes || props.attributes || [], props.supply)
	}, [isBitmap, freshAttributes, props.attributes, props.supply])

	const { numberFilters, attributeFilters } = useMemo(() => splitBitmapFilters(filters), [filters])

	const { data, isLoading: isInscriptionsLoading } = useCollectionInscriptions(
		props.slug,
		(!isRemoteCollection || isVirtualObject) && !props.isFungible
	)
	const { data: escrows, isLoading: isEscrowsLoading } = useEscrows(props.slug)
	const { data: rawRuneData } = useRuneStats(props.slug)
	const runeData = useMemo(() => (isRune(props.slug) ? rawRuneData : {}), [props.slug, rawRuneData])
	const isLoading = isInscriptionsLoading || isEscrowsLoading

	const memoizedEscrowsMap = useMemo(() => {
		const map = {}
		escrows?.forEach((e: any) => {
			;(map as any)[e.inscription_id] = e
		})
		return map
	}, [escrows])
	const collectionInscriptionMap = useMemo(() => {
		const map: Record<string, any> = {}
		if (!Array.isArray(data)) return map
		for (const inscription of data) {
			if (inscription?.id) {
				map[inscription.id] = inscription
			}
		}
		return map
	}, [data])

	// Optimize the sorting function
	const sortInscriptions = useCallback(
		(inscriptions: any, sortType: any, isRune: any, isFungible: any) => {
			// Check if inscriptions is iterable
			if (!inscriptions || !Array.isArray(inscriptions)) {
				console.warn('Inscriptions is not an array:', inscriptions)
				return [] // Return an empty array if inscriptions is not iterable
			}

			return [...inscriptions].sort((a, b) => {
				if (sortType === 'PriceAsc' || sortType === 'PriceDesc') {
					let aPrice, bPrice

					if (isFungible) {
						aPrice =
							(a?.escrow?.satoshi_price || Number.MAX_VALUE) / parseInt(a?.escrow.amount || '1')
						bPrice =
							(b?.escrow?.satoshi_price || Number.MAX_VALUE) / parseInt(b?.escrow.amount || '1')
					} else {
						aPrice = a?.escrow?.satoshi_price || Number.MAX_VALUE
						bPrice = b?.escrow?.satoshi_price || Number.MAX_VALUE
					}

					return sortType === 'PriceAsc' ? aPrice - bPrice : bPrice - aPrice
				}

				if (sortType === 'RankAsc') {
					const aRank = ranksData?.ranks?.[a?.id]?.rank ?? a?.meta?.rank ?? Number.MAX_VALUE
					const bRank = ranksData?.ranks?.[b?.id]?.rank ?? b?.meta?.rank ?? Number.MAX_VALUE
					return aRank - bRank
				}
				if (sortType === 'RankDesc') {
					const aRank = ranksData?.ranks?.[a?.id]?.rank ?? a?.meta?.rank ?? 0
					const bRank = ranksData?.ranks?.[b?.id]?.rank ?? b?.meta?.rank ?? 0
					return bRank - aRank
				}
				if (sortType === 'InscriptionAsc') {
					return getInscriptionSortValue(a) - getInscriptionSortValue(b)
				}
				if (sortType === 'InscriptionDesc') {
					return getInscriptionSortValue(b) - getInscriptionSortValue(a)
				}
				if (sortType === 'ListedDesc') {
					const aTime = a?.escrow?.created ? parseDate(a.escrow.created).getTime() : 0
					const bTime = b?.escrow?.created ? parseDate(b.escrow.created).getTime() : 0
					return bTime - aTime
				}

				return 0
			})
		},
		[ranksData]
	)

	const updateFilters = useCallback((trait_type: string, values: string[]) => {
		setFilters((prev) => {
			const index = prev.findIndex((e) => e.trait_type === trait_type)
			if (values?.length === 0) {
				if (index !== -1) {
					prev.splice(index, 1)
					return [...prev]
				}
				return prev
			} else {
				const current = { trait_type, values }
				if (index !== -1) {
					prev[index] = current
					return [...prev]
				} else {
					return [...prev, current]
				}
			}
		})
	}, [setFilters])

	// Fetch matching inscription IDs from server for bitmap search/filters
	const bitmapSearchKey = useMemo(() => {
		if (!isBitmap || (!debouncedSearch && attributeFilters.length === 0)) return null
		const attributes = expandBitmapAttributeFilters(attributeFilters)
		const satributes = attributeFilters
			?.filter((f) => f.trait_type === 'Satributes')
			?.map((f) => f.values)
			?.flat()
		let url = `${API_URL}/collection/${props.slug}/inscriptions?offset=0&order=PriceAsc&listed=true&limit=200`
		if (debouncedSearch) {
			url += `&search=${encodeURIComponent(debouncedSearch)}`
		}
		const filter: any = {}
		if (attributes?.length > 0) filter.attributes = attributes
		if (satributes?.length > 0) filter.satributes = satributes
		return { url, filter }
	}, [isBitmap, debouncedSearch, attributeFilters, props.slug])

	const { data: bitmapSearchResults } = useSWR(
		bitmapSearchKey,
		({ url, filter }) =>
			fetch(url, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(filter)
			}).then((r) => r.json()),
		{ keepPreviousData: true }
	)

	const bitmapMatchIds = useMemo(() => {
		if (!bitmapSearchResults || !Array.isArray(bitmapSearchResults)) return null
		return new Set(bitmapSearchResults.map((i: any) => i.id))
	}, [bitmapSearchResults])

	const inscriptions = useMemo(() => {
		if (!escrows || !escrows?.length) {
			return []
		}

		let res = []

		if (isRune(props.slug)) {
			res = escrows?.map((e: any) => ({
				...runeData,
				...e,
				collection: { slug: props.slug },
				escrow: e,
				id: e.outpoint,
				ticker: runeData?.name
			}))
		} else if (isAlkanes(props.slug)) {
			res = escrows?.map((e: any) => ({
				...e,
				collection: { slug: props.slug },
				escrow: e,
				id: e.outpoint,
				ticker: props.slug.split('-')[1].toUpperCase()
			}))
		} else if (props.isFungible) {
			res = escrows?.map((e: any) => ({
				...e,
				collection: { slug: props.slug },
				escrow: e,
				id: e.inscription_id
			}))
		} else if (isRemoteCollection) {
			if (isVirtualObject && showAllMobileGrid) {
				res = data?.map((e: any) => ({
					...e,
					collection: e.collection || { slug: props.slug },
					escrow: (memoizedEscrowsMap as any)[e.id]
				}))
				return sortInscriptions(res || [], selected, isRune(props.slug), props.isFungible)
			}

			// Deduplicate escrows by inscription_id to prevent duplicate listings
			const escrowMap: Record<string, any> = {}
			escrows.forEach((e: any) => {
				if (!escrowMap[e.inscription_id]) {
					escrowMap[e.inscription_id] = e
				}
			})
			const uniqueEscrows = Object.values(escrowMap)
			res = uniqueEscrows.map((e: any) => {
				const hydrated = collectionInscriptionMap[e.inscription_id]
				const hydratedMeta = hydrated?.meta || {}
				return {
					...hydrated,
					id: e.inscription_id,
					collection: hydrated?.collection || { slug: props.slug },
					content_type: hydrated?.content_type,
					effective_content_type: hydrated?.effective_content_type,
					// Preserve master's bitmap-aware sort value (handles "<n>.bitmap"
					// names) while preferring hydrated data when we have it.
					num: getInscriptionSortValue(hydrated || e),
					meta: {
						...hydratedMeta,
						name: hydratedMeta?.name || e.name,
						attributes: hydratedMeta?.attributes || []
					},
					name: hydrated?.name || e.name,
					escrow: e
				}
			})
		} else {
			res = data?.map((e: any) => ({
				...e,
				escrow: (memoizedEscrowsMap as any)[e.id]
			}))

			// The mobile grid is the only full-collection browse surface on a phone
			// (desktop has the separate center grid), so it mirrors desktop's "Show
			// All" default and lists every NFT — listed + unlisted. The list view
			// stays for-sale-only, matching desktop's left-column buy list.
			if (selectedTab == 'buy' && !showAllMobileGrid) {
				res = res?.filter((e: any) => e?.escrow && e.escrow.satoshi_price)
			}
		}

		return sortInscriptions(res, selected, isRune(props.slug), props.isFungible)
	}, [
		escrows,
		props.slug,
		props.isFungible,
		isRemoteCollection,
		isVirtualObject,
		sortInscriptions,
		selected,
		runeData,
		data,
		collectionInscriptionMap,
		selectedTab,
		memoizedEscrowsMap,
		showAllMobileGrid
	])

	const filteredInscriptions = useMemo(() => {
		let res = props.slug === 'nodemonkes' ? inscriptions.filter((i: any) => matchesDiscoveryFilters(i.id, filters, savedMonkes, monkeCatalog)) : inscriptions

		if (isBitmap) {
			if (debouncedSearch || attributeFilters.length > 0) {
				if (!bitmapMatchIds) return res
				res = res.filter((i: any) => bitmapMatchIds.has(i.id))
			}
			if (numberFilters.length > 0) {
				res = res.filter((i: any) => matchesBitmapNumberFilters(i, numberFilters))
			}
		}

		// Rarity-tier filter (applies to any collection with rank data)
		const rarityFilter = filters.find((f) => f?.trait_type === RARITY_FILTER_TRAIT)
		if (rarityFilter && rarityFilter.values?.length > 0 && ranksData?.ranks) {
			res = res.filter((i: any) => {
				const tierLabel = ranksData.ranks[i?.id]?.tier?.label
				return tierLabel && rarityFilter.values.includes(tierLabel)
			})
		}

		// Per-trait filters (selected via TraitFilters accordion)
		const traitFilters = filters.filter(
			(f) => f?.trait_type && f.trait_type !== RARITY_FILTER_TRAIT && !isDiscoveryFilter(f.trait_type)
		)
		if (traitFilters.length > 0) {
			res = res.filter((i: any) => {
				const satributes = i?.satributes?.map((s: any) => ({
					trait_type: 'Satributes',
					value: s
				}))
				const attrs = [...(i?.meta?.attributes || []), ...(satributes || [])]
				return traitFilters.every((filter) => matchesBitmapAttributeFilter(filter, attrs))
			})
		}

		return res
	}, [
		props.slug,
		inscriptions,
		debouncedSearch,
		attributeFilters,
		numberFilters,
		isBitmap,
		bitmapMatchIds,
		filters,
		ranksData, savedMonkes, monkeCatalog
	])

	const inscriptionIds = React.useMemo(() => {
		// Only listed items owned by someone else are buyable. The mobile "Show
		// All" grid can include unlisted NFTs (no escrow), so guard on price here
		// to keep the slider / bulk-select from grabbing un-purchasable items.
		return filteredInscriptions.flatMap((i) =>
			i.escrow?.satoshi_price && i.escrow?.seller_address !== Wallet.getTaprootAddress()
				? [i.id]
				: []
		)
	}, [filteredInscriptions])
	const {
		selected: selectedInscriptions,
		onSelect,
		onSliderChange,
		slider,
		setSelected: setSelectSelected,
		setSlider: setSelectSlider
	} = useSelect(inscriptionIds)

	const applyCheckoutSelection = useCallback(
		(ids: string[]) => {
			setInvoiceError(null)
			setSelectSelected(new Set(ids))
			setSelectSlider(ids.length)
			setSelectionOrder(ids)
		},
		[setSelectSelected, setSelectSlider]
	)

	// Advance the multi-step checkout once the purchase modal reports a
	// settled purchase for the current step.
	useEffect(() => {
		const handleCompleted = () => {
			const plan = checkoutPlanRef.current
			if (!plan) return
			const nextIndex = plan.index + 1
			if (nextIndex >= plan.steps.length) {
				checkoutPlanRef.current = null
				setCheckoutPlan(null)
				applyCheckoutSelection([])
				return
			}
			const next = plan.steps[nextIndex]
			checkoutPlanRef.current = { steps: plan.steps, index: nextIndex }
			setCheckoutPlan(checkoutPlanRef.current)
			applyCheckoutSelection(next.ids)
			event.emit('snackbar', {
				title: `${next.ids.length} remaining item${next.ids.length === 1 ? '' : 's'} ready to buy`,
				variant: 'success'
			})
		}
		event.listen('purchase-completed', handleCompleted)
		return () => {
			event.removeListener('purchase-completed', handleCompleted)
		}
	}, [applyCheckoutSelection])

	// Handle initial selection when initialSelectedId or cartItems is provided
	// Cart additions use functional selection updates, preserving existing picks across refreshes.
	const initialSelectionApplied = React.useRef(false)
	const lastCartItemsRef = React.useRef<string[]>([])

	useEffect(() => {
		// Handle single initialSelectedId (legacy)
		const initialSelectedId = props.initialSelectedId
		if (initialSelectedId && !initialSelectionApplied.current && inscriptions.length > 0) {
			const itemExists = inscriptions.some((i) => i.id === initialSelectedId)
			if (itemExists && !selectedInscriptions.has(initialSelectedId)) {
				initialSelectionApplied.current = true
				onSelect(initialSelectedId)
				requestAnimationFrame(() => {
					setSelectionOrder([initialSelectedId])
				})
			}
		}
	}, [props.initialSelectedId, inscriptions, selectedInscriptions, onSelect])

	// Handle cartItems array for multi-selection
	useEffect(() => {
		if (!props.cartItems || props.cartItems.length === 0 || inscriptions.length === 0) return

		// Find new items that weren't in the previous cart
		const newItems = props.cartItems.filter((id) => !lastCartItemsRef.current.includes(id))
		lastCartItemsRef.current = props.cartItems

		// Select any new items
		newItems.forEach((id) => {
			const itemExists = inscriptions.some((i) => i.id === id)
			if (itemExists && !selectedInscriptions.has(id)) {
				onSelect(id)
			}
		})

		// Update selection order to match cart
		if (newItems.length > 0) {
			const nextSelectionOrder = props.cartItems.filter((id) =>
				inscriptions.some((i) => i.id === id)
			)
			requestAnimationFrame(() => {
				setSelectionOrder(nextSelectionOrder)
			})
		}
	}, [props.cartItems, inscriptions, selectedInscriptions, onSelect])

	const handleSelect = useCallback(
		(id: string) => {
			setInvoiceError(null)
			// The user is editing the cart: any in-progress multi-step plan is stale.
			setCheckoutPlan(null)
			const wasSelected = selectedInscriptions.has(id)

			if (featureFlags('app::disable_bulk')) {
				if (wasSelected) {
					setSelectSelected(new Set([]))
				} else {
					setSelectSelected(new Set([id]))
				}
			} else {
				onSelect(id)
			}

			setSelectionOrder((prev) => {
				if (wasSelected) {
					return prev.filter((item) => item !== id)
				} else {
					return featureFlags('app::disable_bulk') ? [id] : [...prev, id]
				}
			})

			if (!wasSelected) {
				setRecentlySelected((prev) => {
					const newSet = new Set(prev)
					newSet.add(id)
					return newSet
				})
				setTimeout(() => {
					setRecentlySelected((prev) => {
						const newSet = new Set(prev)
						newSet.delete(id)
						return newSet
					})
				}, 300)
			}
		},
		[onSelect, selectedInscriptions, setSelectSelected]
	)

	useEffect(() => {
		// Skip escrow validation in minimal mode - we trust the initial selection
		if (props.minimal) return

		const handleEscrowChanges = () => {
			let orderChanged = false
			const newOrder = selectionOrder.filter((id) => {
				const item = inscriptions.find((i) => i.id === id)
				if (!item || !item.escrow) {
					orderChanged = true
					onSelect(id) // This will toggle the selection off
					return false
				}
				return true
			})

			if (orderChanged) {
				requestAnimationFrame(() => {
					setSelectionOrder(newOrder)
				})
			}
		}

		handleEscrowChanges()

		const handleEvent = (payload: any) => {
			const item = inscriptions.find((i) => i.id === payload.id)
			if (item && item.escrow?.seller_address !== Wallet.getTaprootAddress()) {
				handleSelect(payload.id)
			}
		}

		event.listen(EVENT, handleEvent)

		return () => {
			event.removeListener(EVENT, handleEvent)
		}
	}, [inscriptions, onSelect, handleSelect, selectionOrder, props.minimal])

	// Notify parent when selection count changes
	useEffect(() => {
		props.onSelectionCountChange?.(selectedInscriptions.size)
	}, [selectedInscriptions, props.onSelectionCountChange, props])

	const buildInvoice = useCallback(
		async (deps: any[], feeRate: number) => {
			const selectedInscriptionArray = deps[0] as string[]

			if (feeRate && selectedInscriptionArray.length > 0) {
				const collectionSlug = sanitizeCollectionSlug(props.slug)
				const assetProtocol: WalletTelemetryAssetProtocol = isRune(props.slug)
					? 'rune'
					: isAlkanes(props.slug)
					? 'alkane'
					: props.slug.toLowerCase().startsWith('tap-')
					? 'tap'
					: props.slug.toLowerCase().startsWith('brc20-')
					? 'brc20'
					: 'ordinal'
				const telemetryDetails = {
					...tapProviderDetails(),
					...(collectionSlug ? { collection_slug: collectionSlug } : {}),
					asset_protocol: assetProtocol,
					item_count_bucket: itemCountBucket(selectedInscriptionArray.length)
				}
				const telemetryFlow =
					Wallet.type() === 'tap'
						? beginWalletFlow(
								'purchase_build',
								'tap',
								'collection_feed',
								'build_psbt',
								telemetryDetails
						  )
						: null
				const finishBuild = (outcome: 'success' | 'failure' | 'cancelled', error?: unknown) => {
					if (telemetryFlow) {
						finishWalletFlow(telemetryFlow, 'build_psbt', outcome, {
							...telemetryDetails,
							...(outcome === 'failure' ? { error_category: classifyWalletError(error) } : {})
						})
					}
				}
				setIsRebuilding(true)
				setInvoiceError(null) // Clear the error when rebuilding starts
				// Which settlement path applies is a property of the listings being
				// bought, not of the deployment: a snipe-protected listing must settle
				// through its passthrough sale, everything else stays on the legacy path.
				const selectedItems = selectedInscriptionArray.map((id) =>
					inscriptions.find((i) => i.id === id)
				)
				// A cart that mixes legacy and protected listings cannot settle in
				// one transaction: the legacy bulk builder refuses protected rows,
				// and a protected purchase is its own chain of sale packages (one
				// passthrough parent + sale child per item, capped per purchase).
				// Freeze the plan and narrow the selection to its
				// first step; the selection change rebuilds this invoice for that
				// step alone and `purchase-completed` walks the rest.
				const plan = planCheckout(selectedItems)
				if (isMultiStepCheckout(plan) && !checkoutPlanRef.current) {
					checkoutPlanRef.current = { steps: plan, index: 0 }
					setCheckoutPlan(checkoutPlanRef.current)
					applyCheckoutSelection(plan[0].ids)
					finishBuild('cancelled')
					setIsRebuilding(false)
					return { psbt: '', fee: BigInt(0) }
				}
				const secureSelected = selectedItems.some((item) => isSnipeProtectedListing(item))
				// A quote belongs to the build that produced it. Without this,
				// editing a mixed cart down to legacy-only items left the old
				// protected quote in place and submit took the protected path
				// for items that were no longer selected.
				secureQuoteRef.current = null
				try {
					if (secureSelected) {
						const protocol = protocolFromSlug(props.slug, {
							rune: Boolean(isRune(props.slug)),
							alkane: Boolean(isAlkanes(props.slug))
						})
						const outpoints = await Promise.all(
							selectedItems.map((item) =>
								resolveBitcoinOutpoint(String(item?.outpoint || item?.escrow?.outpoint || item?.id))
							)
						)
						const quote = await buildSecurePurchase({
							outpoints,
							protocol,
							feeRate,
							listings: selectedItems.map((item) => ({
								sellerAddress: String(item?.escrow?.seller_address || ''),
								creatorAddress: item?.escrow?.creator_address,
								satoshiPrice: Number(item?.escrow?.satoshi_price) || undefined
							}))
						})
						secureQuoteRef.current = quote
						setBuiltPsbt(quote.purchases[0]?.psbt || quote.ticketHex)
						builtSetupRef.current = null
						setBuiltFee(quote.totals.miner_fee_sats)
						setBuiltRebarFee(0)
						setBuiltRebar(false)
						finishBuild('success')
						return {
							psbt: quote.purchases[0]?.psbt || quote.ticketHex,
							fee: BigInt(quote.totals.miner_fee_sats)
						}
					}
					if (isRune(props.slug)) {
						const data = await Wallet.getRunePurchases(
							selectedInscriptionArray.map((id) => inscriptions.find((i) => i.id === id).outpoint),
							feeRate
						)
						console.log(
							`[v2-feed] rune quote slug=${props.slug} networkFee=${Number(
								data.purchase_fee
							)} rebarFee=${data.rebar_fee}`
						)
						setBuiltPsbt(data.psbt)
						builtSetupRef.current = null
						setBuiltFee(Number(data.purchase_fee))
						setBuiltRebarFee(data.rebar_fee)
						setBuiltRebar(data.rebar)
						finishBuild('success')
						return { psbt: data.psbt, fee: data.purchase_fee }
					} else if (isAlkanes(props.slug)) {
						const data = await Wallet.getAlkanePurchases(
							selectedInscriptionArray.map((id) => inscriptions.find((i) => i.id === id).outpoint),
							feeRate
						)
						console.log(
							`[v2-feed] alkane quote slug=${props.slug} networkFee=${Number(
								data.purchase_fee
							)} rebarFee=${data.rebar_fee}`
						)
						setBuiltPsbt(data.psbt)
						builtSetupRef.current = null
						setBuiltFee(Number(data.purchase_fee))
						setBuiltRebarFee(data.rebar_fee)
						setBuiltRebar(data.rebar)
						finishBuild('success')
						return { psbt: data.psbt, fee: data.purchase_fee }
					} else {
						const data = await Wallet.getPurchases(selectedInscriptionArray, feeRate)
						const totalFee = Number(data.setup_fee) + Number(data.purchase_fee)
						console.log(
							`[v2-feed] quote slug=${props.slug} setupFee=${Number(
								data.setup_fee
							)} purchaseFee=${Number(data.purchase_fee)} networkFee=${totalFee} rebarFee=${
								data.rebar_fee
							}`
						)
						setBuiltPsbt(data.purchase)
						builtSetupRef.current = data.setup
						setBuiltFee(Number(totalFee))
						setBuiltRebarFee(data.rebar_fee)
						setBuiltRebar(data.rebar)
						finishBuild('success')
						return { psbt: data.purchase, setup: data.setup, fee: BigInt(totalFee) }
					}
				} catch (e) {
					// Ignore aborted/superseded requests - they're expected when user changes selection
					if (
						axios.isCancel(e) ||
						(e as any)?.code === 'ERR_CANCELED' ||
						(e as any)?.name === 'CanceledError' ||
						(e as any)?.message === 'Request superseded'
					) {
						finishBuild('cancelled')
						return { psbt: '', fee: BigInt(0) }
					}

					finishBuild('failure', e)
					console.error('Error building invoice:', e)
					const msg = secureSelected
						? secureErrorMessage(e)
						: (e as any)?.response?.data?.message ||
						  (typeof (e as any)?.response?.data === 'string' ? (e as any).response.data : '') ||
						  (e as any)?.message ||
						  'An error occurred while building the invoice'
					setInvoiceError(msg)
					return { psbt: '', fee: BigInt(0) }
				} finally {
					setIsRebuilding(false)
				}
			}
			return { psbt: '', fee: BigInt(0) }
		},
		[inscriptions, props.slug, applyCheckoutSelection]
	)

	const totalPrice = React.useMemo(() => {
		return Array.from(selectedInscriptions).reduce((total, id) => {
			const inscription = inscriptions.find((i) => i.id === id)
			return total + (inscription?.escrow?.satoshi_price || 0)
		}, 0)
	}, [selectedInscriptions, inscriptions])

	const totalPriceBTC = totalPrice / 1e8

	const marketFeeBTC = totalPriceBTC * MARKET_FEE_RATE
	const marketFeeUSD = marketFeeBTC * exchangeRate

	const subtotalBTC = totalPriceBTC - marketFeeBTC
	const subtotalUSD = subtotalBTC * exchangeRate

	const totalAmount = React.useMemo(() => {
		return Array.from(selectedInscriptions).reduce((total, id) => {
			const inscription = inscriptions.find((i) => i.id === id)
			return (
				total +
				(props.isFungible
					? parseInt(inscription?.meta?.attributes?.[0]?.value || inscription?.amount || '0')
					: 1)
			)
		}, 0)
	}, [selectedInscriptions, inscriptions, props.isFungible])

	const invoiceBreakdown = useMemo(() => {
		if (!builtPsbt) return null
		// A protected purchase is a chain of sale packages (passthrough parent +
		// sale child per item) with the OW fee in the first sale only, so
		// scanning one PSBT for fee outputs is meaningless. The locally verified
		// quote totals are authoritative for protected buys.
		const secure = secureQuoteRef.current
		if (secure) {
			try {
				const parsed = secureInvoice(secure)
				return {
					marketFeeSat: parsed.marketFeeSat,
					toolFeeSat: 0,
					sellerProceedsSat: parsed.sellerProceedsSat,
					creatorRoyaltySat: parsed.creatorRoyaltySat,
					itemTotalSat: parsed.itemTotalSat,
					networkFeeSat: parsed.networkFeeSat,
					rebarFeeSat: 0,
					totalSat: parsed.totalSat
				}
			} catch (e) {
				console.error('[v2-feed] secure invoice parse failed', e)
				return null
			}
		}
		const selectedItems = Array.from(selectedInscriptions).map((id) =>
			inscriptions.find((i) => i.id === id)
		)
		try {
			// const marketFeeSat = Wallet.getMarketFee(builtPsbt)
			const marketFeeSat = Wallet.getOutputAmountToAddress(builtPsbt, MARKET_ROYALTY_ADDRESS)
			const toolFeeSat = Wallet.getOutputAmountToAddress(builtPsbt, TOOL_FEE_ADDRESS)
			const uniqueSellerAddresses = Array.from(
				new Set(
					selectedItems.flatMap((item) =>
						item?.escrow?.seller_address ? [item.escrow.seller_address] : []
					)
				)
			)
			const sellerProceedsSat = uniqueSellerAddresses
				.map((addr) => Wallet.getOutputAmountToAddress(builtPsbt, addr))
				.reduce((a, v) => a + (v || 0), 0)
			const uniqueCreatorAddresses = Array.from(
				new Set(
					selectedItems.flatMap((item) =>
						item?.escrow?.creator_address ? [item.escrow.creator_address] : []
					)
				)
			)
			const creatorRoyaltySat = uniqueCreatorAddresses
				.map((addr) => Wallet.getOutputAmountToAddress(builtPsbt, addr))
				.reduce((a, v) => a + (v || 0), 0)
			const itemTotalSat = sellerProceedsSat + marketFeeSat + creatorRoyaltySat + toolFeeSat
			const totalSat = itemTotalSat + builtFee + builtRebarFee
			console.log(
				`[v2-feed] breakdown slug=${props.slug} itemTotal=${itemTotalSat} subtotal=${sellerProceedsSat} marketFee=${marketFeeSat} royalty=${creatorRoyaltySat} networkFee=${builtFee} rebarFee=${builtRebarFee} total=${totalSat}`
			)
			return {
				marketFeeSat,
				toolFeeSat,
				sellerProceedsSat,
				creatorRoyaltySat,
				itemTotalSat,
				networkFeeSat: builtFee,
				rebarFeeSat: builtRebarFee,
				totalSat
			}
		} catch (_e) {
			return null
		}
	}, [builtPsbt, selectedInscriptions, inscriptions, builtFee, builtRebarFee, props.slug])

	const invoiceItems = [
		{
			title: 'Total',
			valueFunc: ({ building, fee }: any) => {
				if (building) return '--'
				if (invoiceBreakdown) {
					const totalBTC = invoiceBreakdown.totalSat / 1e8
					const totalUSD = totalBTC * exchangeRate
					return `${totalBTC.toFixed(8)} ${NETWORK} / $${numberWithCommas(totalUSD.toFixed(2))}`
				}
				const networkFeeBTC = Number(fee) / 1e8
				const rebarFeeBTC = builtRebarFee / 1e8
				const totalWithFeeBTC = totalPriceBTC + networkFeeBTC + rebarFeeBTC
				const totalWithFeeUSD = totalWithFeeBTC * exchangeRate
				return `${totalWithFeeBTC.toFixed(8)} ${NETWORK} / $${numberWithCommas(
					totalWithFeeUSD.toFixed(2)
				)}`
			}
		},
		{
			title: 'Subtotal',
			valueFunc: () => {
				if (invoiceBreakdown) {
					const btc = invoiceBreakdown.sellerProceedsSat / 1e8
					const usd = btc * exchangeRate
					return `${btc.toFixed(8)} ${NETWORK} / $${numberWithCommas(usd.toFixed(2))}`
				}
				return `${subtotalBTC.toFixed(8)} ${NETWORK} / $${numberWithCommas(subtotalUSD.toFixed(2))}`
			}
		},
		{
			title: `Market Fee (${MARKET_FEE_PERCENTAGE}%)`,
			valueFunc: () => {
				if (invoiceBreakdown) {
					const btc = invoiceBreakdown.marketFeeSat / 1e8
					const usd = btc * exchangeRate
					return `${btc.toFixed(8)} ${NETWORK} / $${numberWithCommas(usd.toFixed(2))}`
				}
				return `${marketFeeBTC.toFixed(8)} ${NETWORK} / $${numberWithCommas(
					marketFeeUSD.toFixed(2)
				)}`
			}
		},
		{
			title: 'Network Fee'
			// Note: Do not add a value for the network fee here. It will be calculated dynamically when the invoice is built.
		},
		...snipeProtectionInvoiceItem(!!secureQuoteRef.current),
		...(builtRebar && builtRebarFee
			? [
					{
						title: 'Mempool Protection',
						valueFunc: () => {
							const btc = builtRebarFee / 1e8
							const usd = btc * exchangeRate
							return `${btc.toFixed(8)} ${NETWORK} / $${numberWithCommas(usd.toFixed(2))}`
						}
					}
			  ]
			: [])
	]

	const loadingRef = React.useRef(false)
	const [_calculating, _setCalculating] = useState(false)

	const handleSubmit = useCallback(
		async (psbt: string, setup?: string) => {
			loadingRef.current = true

			if (secureQuoteRef.current) {
				const quote = secureQuoteRef.current
				if (!quote) {
					loadingRef.current = false
					return
				}
				const selectedItems = Array.from(selectedInscriptions).map((id) =>
					inscriptions.find((i) => i.id === id)
				)
				// Same order as a legacy purchase: the wallet signs now, the
				// modal shows the invoice and broadcasts on confirmation.
				let secureSigned
				try {
					secureSigned = await signSecurePurchase(quote)
				} catch (e) {
					console.error('Error signing purchase:', e)
					loadingRef.current = false
					throw e
				}
				event.emit('open-purchase-modal', {
					inscriptions: selectedItems,
					purchaseData: {
						secureQuote: quote,
						secureSigned,
						// Parsed from the PSBTs (invoiceBreakdown); API totals only as fallback.
						networkFee: invoiceBreakdown?.networkFeeSat ?? quote.totals.miner_fee_sats,
						marketFee: invoiceBreakdown?.marketFeeSat ?? quote.totals.ow_fee_sats,
						totalPrice: invoiceBreakdown?.totalSat ?? quote.totals.total_sats,
						subtotalPrice: invoiceBreakdown?.sellerProceedsSat ?? quote.totals.seller_sats,
						creatorRoyalty:
							invoiceBreakdown?.creatorRoyaltySat ?? quote.totals.creator_royalty_sats,
						rune: isRune(props.slug),
						alkane: isAlkanes(props.slug),
						slug: props.slug
					}
				})
				loadingRef.current = false
				return
			}

			let signedSetup, signedPurchase, success

			if (isRune(props.slug) || isAlkanes(props.slug)) {
				const res = await Wallet.signPSBT(psbt)
				signedPurchase = res.psbt
				success = res.success
			} else {
				try {
					const res = await Wallet.signPurchase(setup as any, psbt)
					signedSetup = res.signedSetup
					signedPurchase = res.signedPurchase
					success = res.success
				} catch (e) {
					console.error('Error signing purchase:', e)
					throw e
				}
			}

			if (success) {
				const setupFee = signedSetup ? Wallet.getPSBTFee(signedSetup) : BigInt(0)
				let purchaseFee = 0
				if (builtRebar) {
					purchaseFee = 0
				} else {
					purchaseFee = Number(Wallet.getPSBTFee(signedPurchase as any))
				}
				const networkFee = Number(purchaseFee) + Number(setupFee)
				const totalFee = networkFee + builtRebarFee
				console.log(
					`[v2-feed] signed slug=${props.slug} setupFee=${Number(setupFee)} purchaseFee=${Number(
						purchaseFee
					)} networkFee=${networkFee} rebarFee=${builtRebarFee} totalFee=${totalFee}`
				)

				// Use pre-built invoice breakdown (derived from built PSBT) only
				if (!invoiceBreakdown) {
					loadingRef.current = false
					return
				}
				const selectedItems = Array.from(selectedInscriptions).map((id) =>
					inscriptions.find((i) => i.id === id)
				)
				const marketFeeSat = invoiceBreakdown.marketFeeSat
				const sellerProceedsSat = invoiceBreakdown.sellerProceedsSat
				const creatorRoyaltySat = invoiceBreakdown.creatorRoyaltySat
				const itemTotalSat = invoiceBreakdown.itemTotalSat
				const totalPriceSat = itemTotalSat + totalFee

				// Invoice breakdown is computed from the built PSBT before signing via useMemo

				event.emit('open-purchase-modal', {
					inscriptions: selectedItems,
					purchaseData: {
						setup: signedSetup,
						purchase: signedPurchase,
						networkFee,
						marketFee: marketFeeSat,
						totalPrice: totalPriceSat,
						subtotalPrice: sellerProceedsSat,
						creatorRoyalty: creatorRoyaltySat,
						rune: isRune(props.slug),
						alkane: isAlkanes(props.slug),
						slug: props.slug,
						rebar: builtRebar,
						rebarFee: builtRebarFee
					}
				})
				// Handle successful purchase (e.g., clear selection, show confirmation)
			}

			loadingRef.current = false
		},
		[props.slug, builtRebar, builtRebarFee, invoiceBreakdown, selectedInscriptions, inscriptions]
	)

	const handleTabChange = useCallback(
		(value: string) => {
			if (value === 'buy' || value === 'swap') {
				setSelectedTab(value)
			} else if (value === 'sell' || value === 'transfer') {
				event.emit('command', {
					command: {
						featured: false,
						component: CollectionDetail,
						name: props.meta.name,
						args: {
							slug: props.slug,
							tab: value === 'sell' ? 'list' : 'send'
						},
						heading: 'Collections'
					}
				})
			}
		},
		[props.slug, props.meta]
	)

	const handleRowClick = useCallback(
		(item: any) => {
			// Unlisted NFTs (shown only in the mobile "Show All" grid) aren't
			// buyable — open the inscription detail instead of selecting.
			if (!item.escrow?.satoshi_price) {
				openInscriptionModal({ id: item.id, inscription: item })
				return
			}

			const isMyListing = item.escrow?.seller_address === Wallet.getTaprootAddress()

			if (isMyListing) {
				event.emit('command', {
					command: {
						featured: false,
						component: CollectionDetail,
						name: props.meta.name,
						args: {
							slug: props.slug,
							tab: 'delist',
							inscriptionId: item.id
						},
						heading: 'Collections'
					}
				})
			} else {
				handleSelect(item.id)
			}
		},
		[props.meta.name, props.slug, handleSelect, openInscriptionModal]
	)

	const renderTableRow = useCallback(
		(item: any) => {
			let amount = item.amount
			if (props.isFungible && !props.isRune) {
				amount = item.meta?.attributes?.[0]?.value || amount
			}
			const formattedAmount = numberWithCommas(amount)

			let pricePerToken = item.escrow?.satoshi_price
			if (props.isFungible) {
				pricePerToken = item.escrow?.satoshi_price / parseInt(amount)
			}

			const { sats, per } = formatSatsPer(pricePerToken)
			const { usd } = formatUsdPer(pricePerToken * 0.00000001 * exchangeRate)

			const showUsd = Number(usd) >= 0.0000001

			const price = props.isFungible ? numberWithCommas(sats) : formatSatoshis(pricePerToken)
			const priceUnit = props.isFungible ? per : ''

			const showBitcoinIcon = !props.isFungible || per.toLowerCase() !== 'sats'

			const isNat = props.slug === 'tap-DMT-NAT'
			const formattedUsd = isNat
				? formatUsdPerBillion(pricePerToken * 0.00000001 * exchangeRate)
				: formatUsdDisplay(Number(usd))

			const isMyListing = item.escrow?.seller_address === Wallet.getTaprootAddress()

			const isSelected = selectedInscriptions.has(item.id)
			const isRecentlySelected = recentlySelected.has(item.id)

			return (
				<TableRow
					key={item.id}
					className={classNames('cursor-pointer transition-all duration-300', {
						'opacity-60': isMyListing,
						'bg-purple-500 bg-opacity-20 hover:bg-purple-500 hover:bg-opacity-30':
							isSelected && !isMyListing,
						'hover:bg-purple-500 hover:bg-opacity-20': !isSelected && !isMyListing,
						'animate-pulse-fast bg-purple-600 bg-opacity-40': isSelected && isRecentlySelected // Only animate if both selected and recently selected
					})}
					onClick={() => handleRowClick(item)}
				>
					<TableCell className="min-w-0 py-2 pl-2 pr-1">
						<div className="flex min-w-0 items-center gap-1.5 [&>button]:shrink-0">
							{!isMyListing && (
								<SelectButton
									onSelect={() => handleSelect(item.id)}
									selected={selectedInscriptions.has(item.id)}
								/>
							)}
							{props.isFungible ? (
								<div className="flex min-w-0 flex-1 items-center">
									<span>{formattedAmount}</span>
								</div>
							) : (
								<div className="flex min-w-0 flex-1 items-center">
									{props.isMobile ? (
										<div
											onClick={(e) => {
												e.stopPropagation()
												setSelectedImage(item)
												setImageModalOpen(true)
											}}
											role="button"
											tabIndex={0}
											onKeyDown={onKeyboardClick((e) => {
												e.stopPropagation()
												setSelectedImage(item)
												setImageModalOpen(true)
											})}
										>
											<InscriptionContent
												className="inline-block mr-1.5 size-6 shrink-0 cursor-pointer align-middle"
												inscription={item}
											/>
										</div>
									) : (
										<TooltipProvider>
											<Tooltip>
												<TooltipTrigger asChild>
													<div className="shrink-0">
														<InscriptionContent
															className="inline-block mr-1.5 size-6 shrink-0 cursor-pointer align-middle"
															inscription={item}
														/>
													</div>
												</TooltipTrigger>
												<TooltipContent side="right" sideOffset={2} className="p-2">
													<InscriptionContent className="size-[256px]" inscription={item} />
												</TooltipContent>
											</Tooltip>
										</TooltipProvider>
									)}
									{isRemoteCollection && !item?.escrow?.name ? (
										<button
											type="button"
 title={item.meta?.name || item?.escrow?.name || `Inscription #${item.num}`}
											onClick={(e) => {
												e.stopPropagation()
												openInscriptionModal({ id: item.id, inscription: item })
											}}
											className="min-w-0 truncate text-xs hover:text-primary hover:underline"
										>
											{item.id.slice(0, 6)}...{item.id.slice(-6)}
										</button>
									) : (
										<button
											type="button"
 title={item.meta?.name || item?.escrow?.name || `Inscription #${item.num}`}
											onClick={(e) => {
												e.stopPropagation()
												openInscriptionModal({ id: item.id, inscription: item })
											}}
											className="min-w-0 truncate text-xs hover:text-primary hover:underline text-left"
										>
											{item.meta?.name || item?.escrow?.name || `Inscription #${item.num}`}
										</button>
									)}
									{/* Beside the name, not the price: in the narrow price column the
									    shield wrapped protected prices onto a second line, so rows
									    looked different per collection. */}
									<SecureListingBadge listing={item} className="ml-1" iconClassName="size-3" />
								</div>
							)}
						</div>
					</TableCell>
					<TableCell className="text-right py-2 pl-1 pr-3 tabular-nums">
						<div className="flex flex-wrap items-center justify-end gap-x-1">
							{showBitcoinIcon && <BitcoinIcon className="size-3 shrink-0" />}
							<span className="text-xs [overflow-wrap:anywhere]">
								{price} {priceUnit}
							</span>
						</div>
						{showUsd && (
							<div className="text-[10px] text-muted-foreground [overflow-wrap:anywhere]">
								{isNat ? formattedUsd : `$${formattedUsd}`}
							</div>
						)}
					</TableCell>
					{props.isFungible && (
						<TableCell className="text-right py-2 pl-[10px] pr-3 tabular-nums">
							<div className="flex items-center justify-end gap-1">
								<BitcoinIcon className="size-3 shrink-0" />
								<span className="text-xs whitespace-nowrap">{formatSatoshis(item.escrow?.satoshi_price)}</span>
							</div>
							<div className="text-[10px] text-muted-foreground [overflow-wrap:anywhere]">
								${formatUsdDisplay(item.escrow?.satoshi_price * 0.00000001 * exchangeRate || 0)}
							</div>
						</TableCell>
					)}
				</TableRow>
			)
		},
		[
			props.isFungible,
			props.isRune,
			props.slug,
			props.isMobile,
			exchangeRate,
			selectedInscriptions,
			recentlySelected,
			isRemoteCollection,
			handleRowClick,
			handleSelect,
			openInscriptionModal
		]
	)

	const renderGridCard = useCallback(
		(item: any) => {
			const pricePerToken = item.escrow?.satoshi_price
			const isListed = !!pricePerToken
			const { usd } = formatUsdPer(pricePerToken * 0.00000001 * exchangeRate)
			const isMyListing = item.escrow?.seller_address === Wallet.getTaprootAddress()
			const isSelected = selectedInscriptions.has(item.id)
			const displayName = item.meta?.name || item?.escrow?.name || `Inscription #${item.num}`
			const idLabel =
				typeof item.num === 'number'
					? `#${item.num}`
					: `${item.id?.slice(0, 6)}…${item.id?.slice(-4)}`
			const rankEntry = ranksData?.ranks?.[item.id] || null

			return (
				<TiltCard
					enabled={isBitmap}
					key={item.id}
					onClick={() => handleRowClick(item)}
					className={classNames(
						'group relative flex cursor-pointer flex-col overflow-hidden rounded-lg border border-border bg-foreground/[0.02] transition-colors',
						{
							'opacity-50': isMyListing,
							'border-purple-400/60 bg-purple-500/15': isSelected && !isMyListing,
							'hover:border-foreground/15 hover:bg-foreground/[0.04]': !isSelected && !isMyListing
						}
					)}
					role="button"
					tabIndex={0}
					onKeyDown={onKeyboardClick(() => handleRowClick(item))}
				>
					<div className="relative aspect-square w-full overflow-hidden bg-background">
						{isBitmap ? (
							<BitmapArt height={displayName} className="size-full" showTxCount />
						) : (
							<InscriptionContent className="size-full object-cover" inscription={item} />
						)}
					</div>
					<div className="flex flex-col gap-1 p-3">
						<div className="flex items-center justify-between gap-2 min-w-0">
							<button
								type="button"
								onClick={(e) => {
									e.stopPropagation()
									openInscriptionModal({ id: item.id, inscription: item })
								}}
								className="truncate text-sm font-semibold text-foreground hover:text-primary hover:underline text-left min-w-0"
							>
								{displayName}
							</button>
							{rankEntry?.tier && (
								<span
									className={classNames(
										'hidden md:inline-flex shrink-0 items-center rounded-full px-1.5 text-[9px] leading-[18px] font-semibold whitespace-nowrap',
										rankEntry.tier.className
									)}
								>
									{rankEntry.tier.label}
								</span>
							)}
						</div>
						<div className="flex items-center justify-between gap-2 min-w-0">
							<div className="truncate text-[11px] text-muted-foreground">{idLabel}</div>
							{rankEntry && (
								<span
									className={classNames(
										'shrink-0 text-[9px] font-semibold tabular-nums whitespace-nowrap',
										rankEntry?.tier?.textClassName
									)}
								>
									Rank: #{numberWithCommas(rankEntry.rank.toString())}
								</span>
							)}
						</div>
						{isListed && !isMyListing ? (
							<div className="mt-1">
								<InscriptionActions inscription={item} owner={false} actionButtonColor="black"
									onlyShowPrice hideView selected={isSelected}
									collectionCard={{ slug: props.slug, name: props.meta.name }}
									onClickBuy={() => handleSelect(item.id)} />
							</div>
						) : (
						<div className="mt-1 flex items-end justify-between gap-2">
							<div className="min-w-0">
								{isListed ? (
									<>
										<div className="flex items-center gap-1 text-sm font-semibold text-foreground">
											<BitcoinIcon className="size-3 shrink-0" />
											<span className="truncate">{formatSatoshis(pricePerToken)}</span>
										</div>
										<div className="truncate text-[11px] text-muted-foreground">
											${formatUsdDisplay(Number(usd))}
										</div>
									</>
								) : (
									<div className="text-sm font-medium text-muted-foreground">Unlisted</div>
								)}
							</div>

						</div>
						)}
					</div>
				</TiltCard>
			)
		},
		[
			props.slug,
			props.meta.name,
			isBitmap,
			exchangeRate,
			selectedInscriptions,
			handleRowClick,
			handleSelect,
			openInscriptionModal,
			ranksData
		]
	)

	// Stable serialized key of selected inscriptions to prevent unnecessary rebuilds
	const selectedInscriptionsKey = useMemo(() => {
		return Array.from(selectedInscriptions).sort().join(',')
	}, [selectedInscriptions])

	const memoizedBuildDeps = useMemo(() => {
		// Convert serialized key back to array for the build function
		const sortedIds = selectedInscriptionsKey ? selectedInscriptionsKey.split(',') : []
		return [sortedIds]
	}, [selectedInscriptionsKey])

	const sortedInscriptions = React.useMemo(() => {
		return [...filteredInscriptions].sort((a, b) => {
			// First, sort by selection status and maintain selection order
			const aIndex = selectionOrder.indexOf(a.id)
			const bIndex = selectionOrder.indexOf(b.id)

			if (aIndex !== -1 && bIndex !== -1) {
				// Both are selected, sort by selection order
				return aIndex - bIndex
			} else if (aIndex !== -1) {
				// Only a is selected
				return -1
			} else if (bIndex !== -1) {
				// Only b is selected
				return 1
			}

			// If neither is selected, maintain the existing sort order
			if (selected === 'PriceAsc' || selected === 'PriceDesc') {
				let aPrice, bPrice

				if (isRune(props.slug) || isAlkanes(props.slug) || props.isFungible) {
					aPrice =
						(a?.escrow?.satoshi_price || Number.MAX_VALUE) / parseInt(a?.escrow.amount || '1')
					bPrice =
						(b?.escrow?.satoshi_price || Number.MAX_VALUE) / parseInt(b?.escrow.amount || '1')
				} else {
					aPrice = a?.escrow?.satoshi_price || Number.MAX_VALUE
					bPrice = b?.escrow?.satoshi_price || Number.MAX_VALUE
				}

				return selected === 'PriceAsc' ? aPrice - bPrice : bPrice - aPrice
			}

			if (selected === 'RankAsc') {
				const aRank = ranksData?.ranks?.[a?.id]?.rank ?? a?.meta?.rank ?? Number.MAX_VALUE
				const bRank = ranksData?.ranks?.[b?.id]?.rank ?? b?.meta?.rank ?? Number.MAX_VALUE
				return aRank - bRank
			}
			if (selected === 'RankDesc') {
				const aRank = ranksData?.ranks?.[a?.id]?.rank ?? a?.meta?.rank ?? 0
				const bRank = ranksData?.ranks?.[b?.id]?.rank ?? b?.meta?.rank ?? 0
				return bRank - aRank
			}
			if (selected === 'InscriptionAsc') {
				return getInscriptionSortValue(a) - getInscriptionSortValue(b)
			}
			if (selected === 'InscriptionDesc') {
				return getInscriptionSortValue(b) - getInscriptionSortValue(a)
			}
			if (selected === 'ListedDesc') {
				const aTime = a?.escrow?.created ? parseDate(a.escrow.created).getTime() : 0
				const bTime = b?.escrow?.created ? parseDate(b.escrow.created).getTime() : 0
				return bTime - aTime
			}

			return 0
		})
	}, [filteredInscriptions, selectionOrder, selected, props.slug, props.isFungible, ranksData])

	const fetchMoreData = () => {
		setDisplayCount((prevCount) => prevCount + 32)
	}

	const activeFilterCount = useMemo(
		() => filters.reduce((count, filter) => count + (filter.values?.length || 0), 0),
		[filters]
	)
	const hasBitmapFilterControls = isBitmap && bitmapAttributes.length > 0
	const clearFilters = useCallback(() => setFilters([]), [setFilters])

	const hasRanksData = !!ranksData?.ranks
	const collectionTraitGroups = useMemo(
		() => (Array.isArray(props.attributes) ? props.attributes : []),
		[props.attributes]
	)
	const hasCollectionTraits = collectionTraitGroups.length > 0 && !props.isFungible && !isBitmap
	const showFilterControls =
		!props.minimal && !props.isFungible && !isBitmap && (hasRanksData || hasCollectionTraits)
	const rarityTierCounts = useMemo(() => {
		if (!ranksData?.ranks) return null
		const counts: Record<string, number> = {
			Exotic: 0,
			Legendary: 0,
			Epic: 0,
			Rare: 0,
			Uncommon: 0,
			Common: 0
		}
		for (const id in ranksData.ranks) {
			const label = ranksData.ranks[id]?.tier?.label
			if (label && counts[label] !== undefined) counts[label]++
		}
		return counts
	}, [ranksData])
	const filterGroups = useMemo(() => {
		const groups: any[] = []
		if (hasRanksData) {
			groups.push({
				trait_type: RARITY_FILTER_TRAIT,
				values: RARITY_TIER_OPTIONS.map((label) => ({
					trait_type: RARITY_FILTER_TRAIT,
					value: label,
					count: rarityTierCounts?.[label] ?? 0
				}))
			})
		}
		return [...groups, ...collectionTraitGroups]
	}, [hasRanksData, rarityTierCounts, collectionTraitGroups])
	const totalActiveFilterCount = useMemo(
		() => filters.reduce((count, filter) => count + (filter.values?.length || 0), 0),
		[filters]
	)
	const [allFiltersSheetOpen, setAllFiltersSheetOpen] = useState(false)
	const sortOptions = useMemo(() => {
		const isRuneSlug = isRune(props.slug)
		return [
			{ id: 'PriceAsc', label: 'Lowest price' },
			...(hasRanksData ? [{ id: 'RankAsc', label: 'Rarest first' }] : []),
			...(hasRanksData ? [{ id: 'RankDesc', label: 'Most common first' }] : []),
			{ id: 'PriceDesc', label: 'Highest price' },
			...(!isRuneSlug
				? [
						{ id: 'InscriptionAsc', label: 'Lowest inscription' },
						{ id: 'InscriptionDesc', label: 'Highest inscription' }
				  ]
				: []),
			{ id: 'ListedDesc', label: 'Recently listed' }
		]
	}, [hasRanksData, props.slug])

	if (props.slug === 'tap-$GHSY') {
		return (
			<div className="p-4 flex justify-center h-full gap-1">
				new
				<Link className="text-purple-500 underline" href="/collection/tap-$SPKY">
					spooky
				</Link>
				who dis?
			</div>
		)
	}

	return (
		<>
			<div
				className={
					isMobile
						? 'relative flex flex-col md:border-r bg-chrome-muted dark:bg-transparent'
						: 'relative flex flex-col h-full overflow-hidden md:border-r bg-chrome-muted dark:bg-transparent'
				}
				style={isMobile ? undefined : { height: props.height, maxHeight: props.height }}
			>
				{/* Fixed Header - Always rendered */}
				<div className={`flex-shrink-0${isTapDmtNat && selectedTab === 'swap' ? '' : ' border-b'}`}>
					{/* Tabs - hidden in minimal mode */}
					{!props.minimal && (
						<div className="px-3 pt-3">
							{/* Restoring focus after closing Sell/Transfer must not launch it again. */}
							<Tabs activationMode="manual" value={selectedTab} onValueChange={handleTabChange} className="w-full">
								<TabsList className="grid h-8 w-full grid-cols-3 rounded-md bg-foreground/[0.06] dark:bg-white/[0.06] p-0.5 text-foreground/60 dark:text-white/60">
									<TabsTrigger
										value="buy"
										className="h-7 rounded text-xs font-medium transition-colors data-[state=active]:bg-foreground/[0.12] data-[state=active]:dark:bg-white/10 data-[state=active]:text-foreground data-[state=active]:dark:text-white data-[state=active]:shadow-none"
									>
										Buy
									</TabsTrigger>
									<TabsTrigger
										value="sell"
										className="h-7 rounded text-xs font-medium transition-colors data-[state=active]:bg-foreground/[0.12] data-[state=active]:dark:bg-white/10 data-[state=active]:text-foreground data-[state=active]:dark:text-white data-[state=active]:shadow-none"
									>
										Sell
									</TabsTrigger>
									<TabsTrigger
										value={isTapDmtNat ? 'swap' : 'transfer'}
										className="h-7 rounded text-xs font-medium transition-colors data-[state=active]:bg-foreground/[0.12] data-[state=active]:dark:bg-white/10 data-[state=active]:text-foreground data-[state=active]:dark:text-white data-[state=active]:shadow-none"
									>
										{isTapDmtNat ? (
											<span className="flex items-center gap-1.5">
												Swap
												<span className="rounded-full bg-success/20 px-1.5 py-0.5 text-[9px] font-bold text-success ring-1 ring-emerald-400/40">
													New
												</span>
											</span>
										) : (
											'Transfer'
										)}
									</TabsTrigger>
								</TabsList>
							</Tabs>
						</div>
					)}



					{/* Slider - hidden in minimal mode and on swap tab. The List/Grid
					    toggle for NFTs sits to the slider's right with 16px on each
					    side (the parent's px-4 + the gap-4 between the two). */}
					{!props.minimal &&
						!featureFlags('app::disable_bulk') &&
						!(isTapDmtNat && selectedTab === 'swap') && (
							<div className="flex items-center gap-4 border-b p-4">
								<Slider
									value={[slider]}
									max={24}
									step={1}
									onValueChange={([v]) => {
										// Clear custom ordering when using the slider
										setSelectionOrder([])
										// Update selection live while dragging
										onSliderChange([v])
									}}
									className="flex-1"
								/>
								{isMobile && !props.isFungible && selectedTab === 'buy' && (
									<div className="flex shrink-0 items-center gap-1">
										<button
											type="button"
											onClick={() => setViewMode('list')}
											className={classNames(
												'flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors',
												viewMode === 'list'
													? 'bg-foreground/10 text-foreground'
													: 'text-muted-foreground hover:text-foreground'
											)}
											aria-pressed={viewMode === 'list'}
										>
											<ListIcon size={12} />
											List
										</button>
										<button
											type="button"
											onClick={() => setViewMode('grid')}
											className={classNames(
												'flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors',
												viewMode === 'grid'
													? 'bg-foreground/10 text-foreground'
													: 'text-muted-foreground hover:text-foreground'
											)}
											aria-pressed={viewMode === 'grid'}
										>
											<LayoutGrid size={12} />
											Grid
										</button>
									</div>
								)}
							</div>
						)}
					{!props.minimal && isMobile && viewMode !== 'grid' && selectedTab === 'buy' && hasBitmapFilterControls && (
						<div className="border-b px-4 py-2">
							<BitmapExplorerBar
								filters={filters}
								onChange={updateFilters}
								onClear={clearFilters}
								attributes={bitmapAttributes}
								compact
							/>
						</div>
					)}
					{!props.minimal && isMobile && viewMode !== 'grid' && selectedTab === 'buy' && hasBitmapFilterControls && (
						<div className="flex items-center justify-between gap-2 border-b px-4 py-2">
							<Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
								<SheetTrigger asChild>
									<Button
										variant="outline"
										size="sm"
										className="h-8 gap-1.5 px-3 text-xs"
										aria-label="Open bitmap filters"
									>
										<Filter size={13} />
										Filters
										{activeFilterCount > 0 && (
											<span className="rounded-full bg-foreground px-1.5 py-0.5 text-[10px] font-semibold leading-none text-background">
												{activeFilterCount}
											</span>
										)}
									</Button>
								</SheetTrigger>
								<SheetContent
									side="right"
									className="w-[85vw] max-w-[400px] sm:max-w-[540px] max-h-[100vh] overflow-y-auto"
								>
									<SheetHeader>
										<SheetTitle>Filters</SheetTitle>
									</SheetHeader>
									<div className="mt-4 pr-2">
										<TraitFilters
											traits={bitmapAttributes}
											onChange={updateFilters}
											currentFilters={filters}
										/>
									</div>
								</SheetContent>
							</Sheet>
							{activeFilterCount > 0 && (
								<Button
									variant="ghost"
									size="sm"
									onClick={clearFilters}
									className="h-8 px-2 text-xs"
								>
									Clear
								</Button>
							)}
						</div>
					)}
					{showFilterControls && selectedTab === 'buy' && (
						<div className="flex items-center justify-between gap-2 border-b px-4 py-2">
							<Select value={selected} onValueChange={(v) => setSelected(v)}>
								<SelectTrigger className="h-8 w-[150px] text-xs">
									<SelectValue placeholder="Sort by" />
								</SelectTrigger>
								<SelectContent>
									<SelectGroup>
										{sortOptions.map((opt) => (
											<SelectItem key={opt.id} value={opt.id} className="text-xs">
												{opt.label}
											</SelectItem>
										))}
									</SelectGroup>
								</SelectContent>
							</Select>
							<Sheet open={allFiltersSheetOpen} onOpenChange={setAllFiltersSheetOpen}>
								<SheetTrigger asChild>
									<Button
										variant="outline"
										size="sm"
										className="h-8 gap-1.5 px-3 text-xs"
										aria-label="Open filters"
									>
										<Filter size={13} />
										Filters
										{totalActiveFilterCount > 0 && (
											<span className="rounded-full bg-foreground px-1.5 py-0.5 text-[10px] font-semibold leading-none text-background">
												{totalActiveFilterCount}
											</span>
										)}
									</Button>
								</SheetTrigger>
								<SheetContent
									side="right"
									className="w-[85vw] max-w-[400px] sm:max-w-[540px] max-h-[100vh] overflow-y-auto"
								>
									<SheetHeader>
										<SheetTitle>Filters</SheetTitle>
									</SheetHeader>
									<div className="mt-4 pr-2">
										{props.slug === 'nodemonkes' && <MonkeDiscoveryControls filters={filters} onChange={updateFilters} />}
										<TraitFilters
											traits={filterGroups}
											onChange={updateFilters}
											currentFilters={filters}
										/>
									</div>
									{totalActiveFilterCount > 0 && (
										<Button
											variant="ghost"
											size="sm"
											onClick={clearFilters}
											className="mt-4 h-8 px-2 text-xs"
										>
											Clear all
										</Button>
									)}
								</SheetContent>
							</Sheet>
						</div>
					)}
					{!(isTapDmtNat && selectedTab === 'swap') &&
						!(isMobile && viewMode === 'grid' && !props.isFungible && selectedTab === 'buy') && (
							<Table className="w-full table-fixed"><colgroup>{props.isFungible ? <><col style={{ width: "29%" }} /><col style={{ width: "29%" }} /><col style={{ width: "42%" }} /></> : <><col /><col style={{ width: "140px" }} /></>}</colgroup>
								<TableHeader>
									<TableRow className="border-none">
										<TableHead className="py-0.5 pl-2 md:pl-4 text-muted-foreground">
											{props.isFungible ? 'Quantity' : 'Item'}
										</TableHead>
										<TableHead className="text-right py-0.5 pr-2 md:pr-4 text-muted-foreground">
											Price
										</TableHead>
										{props.isFungible && (
											<TableHead className="text-right py-0.5 pr-2 md:pr-4 text-muted-foreground">
												Total
											</TableHead>
										)}
									</TableRow>
								</TableHeader>
							</Table>
						)}
				</div>

				{isTapDmtNat && selectedTab === 'swap' ? (
					<div className="flex-1 min-h-0 overflow-y-auto">
						<TapSwapWrapper compact iconUrl={props.meta?.icon?.replace('turbo', 'cdn')} />
					</div>
				) : (
					<>
						{/* Scrollable Content — grows to fill the remaining column space
				    between the fixed header and the invoice footer. Real
				    overflow-y-auto so InfiniteScroll's scrollableTarget actually
				    receives scroll events (on mobile too — the Invoice uses
				    viewport-fixed positioning and overlays this area). */}
						<div
							className={
								props.minimal
									? 'overflow-y-auto'
									: isMobile
									? selectedInscriptions.size > 0
										? 'pb-48'
										: ''
									: 'flex-1 min-h-0 overflow-y-auto'
							}
							id="scrollableDiv"
							style={props.minimal ? { height: 'auto', maxHeight: '400px' } : undefined}
						>
							{!props.minimal && !isMobile && !props.isFungible && selectedTab === 'buy' && selectedInscriptions.size === 0 && !(isMobile && viewMode === 'grid') && (
								<div className="mx-4 my-3 flex flex-col gap-2">
									<InstantSell slug={props.slug} name={props.meta.name} compact showAmount hideUnavailable className={tradeStyles.buyRowFull} />
									<MakeOfferButton slug={props.slug} name={props.meta.name} className={tradeStyles.buyRowFull} />
								</div>
							)}
							{isBitmap && isMobile && viewMode === 'grid' && selectedTab === 'buy' && !props.minimal ? (
                                <RemoteCollectionFeed title="BITMAP" items={[]} slug="bitmap" supply={0} listed={escrows?.length || 0} meta={{ name: 'BITMAP', slug: 'bitmap' } as any} isMobile={true} attributes={bitmapAttributes} onSelectBulk={item => handleSelect(item.id)} bulkSelected={Object.fromEntries([...selectedInscriptions].map(id => [id, true]))} />
                            ) : isLoading || isExchangeRateLoading ? (
								<CollectionRowsSkeleton />
							) : (
								<InfiniteScroll
									dataLength={displayCount}
									next={fetchMoreData}
									hasMore={!props.minimal && displayCount < sortedInscriptions.length}
									loader={<React.Fragment />}
									{...(isMobile && !props.minimal ? {} : { scrollableTarget: 'scrollableDiv' })}
								>
									{isMobile && viewMode === 'grid' && !props.isFungible && selectedTab === 'buy' ? (
										<div className="grid grid-cols-2 gap-3 p-3 sm:grid-cols-3 md:grid-cols-2 lg:grid-cols-3">
											{!props.minimal && <MarketTile slug={props.slug} name={props.meta.name} />}
											{(props.minimal && props.cartItems && props.cartItems.length > 0
												? sortedInscriptions.filter((i) => props.cartItems!.includes(i.id))
												: sortedInscriptions.slice(0, displayCount)
											).map(renderGridCard)}
										</div>
									) : (
										<Table className="w-full table-fixed" data-listings-table><colgroup>{props.isFungible ? <><col style={{ width: "29%" }} /><col style={{ width: "29%" }} /><col style={{ width: "42%" }} /></> : <><col /><col style={{ width: "140px" }} /></>}</colgroup>
											<TableBody>
												{(props.minimal && props.cartItems && props.cartItems.length > 0
													? sortedInscriptions.filter((i) => props.cartItems!.includes(i.id))
													: sortedInscriptions.slice(0, displayCount)
												).map(renderTableRow)}
											</TableBody>
										</Table>
									)}
								</InfiniteScroll>
							)}
						</div>

						{/* Invoice footer — on desktop, takes its natural height via
				    flex-shrink-0 so the scrollable area above resizes to fit.
				    On mobile Invoice uses its own `fixed` viewport positioning.
				    Hidden entirely when no items are selected so a stale error
				    from a prior attempt doesn't keep the panel visible after
				    the user clears their selection. */}
						{selectedInscriptions.size > 0 && (
							<div className={isMobile ? '' : 'relative flex-shrink-0'}>
								<Invoice
									open={true}
									onSubmit={handleSubmit}
									buildDeps={memoizedBuildDeps}
									onBuild={buildInvoice}
									action={
										props.isFungible
											? `Buy ${numberWithCommas(totalAmount)}`
											: `Buy ${selectedInscriptions.size} Inscription${
													selectedInscriptions.size !== 1 ? 's' : ''
											  }`
									}
									items={invoiceItems}
									errorMessage={isRebuilding ? undefined : invoiceError ?? undefined}
									debounce={1500}
									fixed={isMobile}
								/>
							</div>
						)}
					</>
				)}
			</div>

			<Dialog open={imageModalOpen} onOpenChange={setImageModalOpen}>
				<DialogContent className="sm:max-w-[425px] p-0 bg-transparent border-0">
					{selectedImage && (
						<InscriptionContent
							className="size-full max-h-[80vh] object-contain"
							inscription={selectedImage}
						/>
					)}
				</DialogContent>
			</Dialog>
		</>
	)
}
